import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit, Trash2, Check, Handshake, ArrowLeft, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import transactionService from '../services/transactionService';
import TransactionForm from '../components/TransactionForm';

export default function TransactionsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<any | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const [filterMonth, setFilterMonth] = React.useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = React.useState(new Date().getFullYear());
  const [filterType, setFilterType] = React.useState<'all' | 'credit' | 'debit'>('all');

  const { data: allTransactions = [], isLoading } = useQuery({
    queryKey: ['transactions'],
    queryFn: () => transactionService.list(),
  });

  const filteredData = React.useMemo(() => {
    let result = allTransactions.filter((t: any) => {
      const date = t.dueDate || t.date ? new Date(t.dueDate || t.date) : null;
      const month = t.month != null ? Number(t.month) : (date ? date.getMonth() + 1 : null);
      const year = t.year != null ? Number(t.year) : (date ? date.getFullYear() : null);
      const monthYearMatch = month === filterMonth && year === filterYear;
      const typeMatch = filterType === 'all' || t.type === filterType;
      return monthYearMatch && typeMatch;
    });

    // Sort by status: pending (isPaid=false) first, then paid (isPaid=true)
    // For transactions with same status, maintain insertion order (stable sort)
    result = result.sort((a: any, b: any) => {
      const aIsPaid = a.isPaid === true;
      const bIsPaid = b.isPaid === true;
      
      if (aIsPaid === bIsPaid) {
        // Same status: maintain original order (stable sort)
        return 0;
      }
      
      // Pending (false) comes before Paid (true)
      return aIsPaid ? 1 : -1;
    });

    return result;
  }, [allTransactions, filterMonth, filterYear, filterType]);

  const totalIncome = filteredData
    .filter((t: any) => t.type === 'credit')
    .reduce((sum: number, t: any) => sum + (t.amount || 0), 0);
  const totalExpenses = filteredData
    .filter((t: any) => t.type === 'debit')
    .reduce((sum: number, t: any) => sum + (t.amount || 0), 0);

  const createMutation = useMutation({
    mutationFn: (payload: any) => transactionService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      setShowForm(false);
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Transação criada com sucesso!' } }));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: any) => transactionService.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      setEditing(null);
      setShowForm(false);
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Transação atualizada com sucesso!' } }));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => transactionService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Transação excluída com sucesso!' } }));
    },
  });

  const togglePaidMutation = useMutation({
    mutationFn: ({ id, isPaid }: { id: string; isPaid: boolean }) =>
      transactionService.update(id, { isPaid: !isPaid }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Status atualizado com sucesso!' } }));
    },
  });

  const onSubmit = async (payload: any) => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

  return (
    <Layout title="Transações" icon={Handshake}>
      <div className="flex flex-col h-screen bg-gray-100">
        <div className="hidden md:block bg-white border-b border-gray-200 px-4 md:px-8 py-6 flex-shrink-0">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
                <Handshake className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Transações</h1>
                <p className="text-gray-600 text-sm mt-1">Gerencie suas receitas e despesas</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-2 md:py-4 flex-shrink-0">
          <div className="flex justify-start mb-2 md:mb-4">
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 font-medium w-full md:w-auto text-sm">
              + Nova Transação
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:gap-4">
            <div>
              <div className="flex gap-2">
                <button onClick={() => { setFilterMonth(filterMonth === 1 ? 12 : filterMonth - 1); if (filterMonth === 1) setFilterYear(filterYear - 1); }} className="h-10 w-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-gray-900 bg-white" aria-label="Mês anterior">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <select value={filterMonth} onChange={(e) => setFilterMonth(Number(e.target.value))} className="flex-1 border border-gray-200 px-2 md:px-3 py-1.5 md:py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 appearance-none text-gray-900 bg-white text-sm capitalize">
                  {monthNames.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
                </select>
                <select value={filterYear} onChange={(e) => setFilterYear(Number(e.target.value))} className="border border-gray-200 px-2 md:px-3 py-1.5 md:py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 w-20 md:w-28 appearance-none text-gray-900 bg-white text-sm">
                  {[2023, 2024, 2025, 2026].map((y) => (<option key={y} value={y}>{y}</option>))}
                </select>
                <button onClick={() => { setFilterMonth(filterMonth === 12 ? 1 : filterMonth + 1); if (filterMonth === 12) setFilterYear(filterYear + 1); }} className="h-10 w-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-gray-900 bg-white" aria-label="Próximo mês">
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-2 md:py-4 flex-shrink-0">
          <div className="grid grid-cols-2 gap-2 md:gap-4">
            <div className="bg-green-50 rounded-lg p-2 md:p-4">
              <p className="text-xs md:text-sm text-gray-600">Receitas</p>
              <p className="text-base md:text-2xl font-bold text-green-600">
                {new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(totalIncome)}
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-2 md:p-4">
              <p className="text-xs md:text-sm text-gray-600">Despesas</p>
              <p className="text-base md:text-2xl font-bold text-red-600">
                {new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(totalExpenses)}
              </p>
            </div>
          </div>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">{editing ? 'Editar' : 'Nova'} Transação</h2>
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              <div className="p-6">
                <TransactionForm initial={editing ?? undefined} onCancel={() => { setShowForm(false); setEditing(null); }} onSubmit={onSubmit} />
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : (
              <>
                {/* Mobile View - Cards */}
                <div className="md:hidden space-y-3">
                  {filteredData.map?.((t: any) => (
                    <div key={t.id} className="bg-white border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${t.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {t.type === 'credit' ? 'Receita' : 'Despesa'}
                            </span>
                            {t.type === 'debit' && (
                              <span className={`text-xs ${t.isPaid ? 'text-green-600' : 'text-yellow-600'}`}>
                                {t.isPaid ? '✓ Pago' : '⏳ Pendente'}
                              </span>
                            )}
                          </div>
                          <div className="font-medium text-gray-900 text-sm">{t.description}</div>
                          <div className="text-xs text-gray-500">{new Date(t.dueDate || t.date).toLocaleDateString('pt-BR')}</div>
                        </div>
                        <div className="text-right">
                          <span className={`text-sm font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {`${t.type === 'credit' ? '+' : '-'} ${new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(t.amount || 0)}`}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 pt-2 border-t">
                        {t.type === 'debit' && !t.isPaid && (
                          <button
                            onClick={() => togglePaidMutation.mutate({ id: t.id, isPaid: t.isPaid })}
                            className="px-3 py-1.5 rounded text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100"
                          >
                            Marcar como pago
                          </button>
                        )}
                        {t.type === 'debit' && (
                          <button 
                            onClick={() => { setEditing(t); setShowForm(true); }} 
                            className="p-1.5 rounded hover:bg-gray-100 text-blue-600" 
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button 
                          onClick={() => { if (confirm('Tem certeza que deseja deletar?')) { deleteMutation.mutate(t.id); } }} 
                          className="p-1.5 rounded hover:bg-gray-100 text-red-600" 
                          title="Deletar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop View - Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Descrição</th>
                        <th className="hidden sm:table-cell px-6 py-3 text-left text-sm font-semibold text-gray-900">Categoria</th>
                        <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">Valor</th>
                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map?.((t: any) => (
                        <tr key={t.id} className="border-b hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${t.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {t.type === 'credit' ? 'Receita' : 'Despesa'}
                              </span>
                              <span className="font-medium text-gray-900 text-sm">{t.description}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{new Date(t.dueDate || t.date).toLocaleDateString('pt-BR')}</div>
                          </td>
                          <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-600">{t.category}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex flex-col items-end">
                              <span className={`text-sm font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                {`${t.type === 'credit' ? '+' : '-'} ${new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(t.amount || 0)}`}
                              </span>
                              {t.type === 'debit' && (
                                <span className={`text-xs ${t.isPaid ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {t.isPaid ? '✓ Pago' : '⏳ Pendente'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-1">
                              {t.type === 'debit' && !t.isPaid && (
                                <button
                                  onClick={() => togglePaidMutation.mutate({ id: t.id, isPaid: t.isPaid })}
                                  className="p-1.5 rounded hover:bg-gray-100 text-green-600"
                                  title="Marcar como pago"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              {t.type === 'debit' && (
                                <button 
                                  onClick={() => { setEditing(t); setShowForm(true); }} 
                                  className="p-1.5 rounded hover:bg-gray-100 text-blue-600" 
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              )}
                              <button 
                                onClick={() => { if (confirm('Tem certeza que deseja deletar?')) { deleteMutation.mutate(t.id); } }} 
                                className="p-1.5 rounded hover:bg-gray-100 text-red-600" 
                                title="Deletar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!isLoading && filteredData.length === 0 && (
              <div className="text-center py-8 text-gray-500">Nenhuma transação encontrada para este período</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
